import React, { memo, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider,
} from "@mui/material";
import {
  CalendarToday,
  Menu as MenuIcon,
  Close as CloseIcon,
  BusinessCenter,
  ContactMail,
} from "@mui/icons-material";

const ModernHeader = memo(
  ({
    onBookingClick,
    onServicesClick,
    onContactClick,
    mode = "dark",
    toggleMode,
  }) => {
    const location = useLocation();
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    
    // Detect current page for active state
    const currentPath = location.pathname;
    const hash = location.hash;
    const isBookingPage = currentPath === '/booking';
    const isConfirmationPage = currentPath === '/confirmation';
    const isHomePage = currentPath === '/' || currentPath === '';
    const isServicesActive = isHomePage && (hash === '#services' || hash === '');
    const isContactActive = isHomePage && hash === '#contact';

    useEffect(() => {
      let ticking = false;
      const handleScroll = () => {
        if (!ticking) {
          window.requestAnimationFrame(() => {
            setScrolled(window.scrollY > 20);
            ticking = false;
          });
          ticking = true;
        }
      };
      window.addEventListener("scroll", handleScroll, { passive: true });
      return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const handleDrawerToggle = () => {
      setMobileOpen(!mobileOpen);
    };

    const handleMenuClick = (callback) => {
      if (callback) callback();
      setMobileOpen(false);
    };

    const servicesButtonProps = onServicesClick
      ? { onClick: onServicesClick }
      : { component: "a", href: "#services" };

    const contactButtonProps = onContactClick
      ? { onClick: onContactClick }
      : { component: "a", href: "#contact" };

    return (
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          zIndex: 1100,
          transition: "backdrop-filter 0.3s ease",
          backdropFilter: scrolled ? "blur(24px)" : "blur(12px)",
        }}
        role="navigation"
        aria-label="Primary"
      >
        <Box sx={{ height: 3, background: "linear-gradient(90deg, #52B788 0%, #95D5B2 50%, #52B788 100%)" }} />
        <Toolbar sx={{ minHeight: { xs: 64, md: 76 }, px: { xs: 2, md: 3 } }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 900,
              letterSpacing: "-0.02em",
              color: "#000000",
            }}
          >
            L.N.S. & ASSOCIATES
          </Typography>

          <Box sx={{ flexGrow: 1 }} />

          <Box sx={{ display: { xs: "none", md: "flex" }, gap: 1, alignItems: "center" }}>
            <Button color="inherit" {...servicesButtonProps} aria-label="View services section">
              Services
            </Button>
            <Button color="inherit" {...contactButtonProps} aria-label="View contact section">
              Contact
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={onBookingClick}
              startIcon={<CalendarToday />}
              sx={{ py: 1.25, px: 2.5 }}
              aria-label="Book Slot"
            >
              Book Slot
            </Button>
          </Box>

          <Box sx={{ display: { xs: "flex", md: "none" }, alignItems: "center", gap: 1 }}>
            <IconButton 
              color="inherit" 
              size="large" 
              aria-label="Menu"
              onClick={handleDrawerToggle}
              sx={{ color: "#000000" }}
            >
              <MenuIcon />
            </IconButton>
          </Box>
        </Toolbar>

        {/* Mobile Drawer */}
        <Drawer
          anchor="right"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better mobile performance
          }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: 280,
              pt: 2,
            },
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 2, pb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#2D6A4F" }}>
              Menu
            </Typography>
            <IconButton onClick={handleDrawerToggle} aria-label="Close menu">
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider />
          <List sx={{ pt: 2 }}>
            <ListItem disablePadding>
              <ListItemButton 
                onClick={() => handleMenuClick(onServicesClick)}
                sx={{
                  py: 1.5,
                  backgroundColor: isServicesActive ? "rgba(82, 183, 136, 0.15)" : "transparent",
                  borderLeft: isServicesActive ? "3px solid #52B788" : "3px solid transparent",
                  "&:hover": {
                    backgroundColor: "rgba(82, 183, 136, 0.1)",
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <BusinessCenter sx={{ color: isServicesActive ? "#52B788" : "#2D6A4F" }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Services" 
                  primaryTypographyProps={{
                    fontWeight: isServicesActive ? 700 : 600,
                    color: isServicesActive ? "#52B788" : "#2D6A4F",
                  }}
                />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton 
                onClick={() => handleMenuClick(onContactClick)}
                sx={{
                  py: 1.5,
                  backgroundColor: isContactActive ? "rgba(82, 183, 136, 0.15)" : "transparent",
                  borderLeft: isContactActive ? "3px solid #52B788" : "3px solid transparent",
                  "&:hover": {
                    backgroundColor: "rgba(82, 183, 136, 0.1)",
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <ContactMail sx={{ color: isContactActive ? "#52B788" : "#2D6A4F" }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Contact" 
                  primaryTypographyProps={{
                    fontWeight: isContactActive ? 700 : 600,
                    color: isContactActive ? "#52B788" : "#2D6A4F",
                  }}
                />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton 
                onClick={() => handleMenuClick(onBookingClick)}
                sx={{
                  py: 1.5,
                  mt: 1,
                  mx: 2,
                  borderRadius: 2,
                  backgroundColor: (isBookingPage || isConfirmationPage) ? "#2D6A4F" : "transparent",
                  border: (isBookingPage || isConfirmationPage) ? "none" : "2px solid #52B788",
                  color: (isBookingPage || isConfirmationPage) ? "#ffffff" : "#52B788",
                  boxShadow: (isBookingPage || isConfirmationPage) ? "0 4px 12px rgba(45, 106, 79, 0.4)" : "none",
                  "&:hover": {
                    backgroundColor: (isBookingPage || isConfirmationPage) ? "#1B4D35" : "rgba(82, 183, 136, 0.1)",
                    borderColor: (isBookingPage || isConfirmationPage) ? "none" : "#2D6A4F",
                    color: (isBookingPage || isConfirmationPage) ? "#ffffff" : "#2D6A4F",
                    boxShadow: (isBookingPage || isConfirmationPage) ? "0 6px 16px rgba(45, 106, 79, 0.5)" : "0 2px 8px rgba(82, 183, 136, 0.2)",
                  },
                }}
              >
                <CalendarToday sx={{ mr: 1, color: (isBookingPage || isConfirmationPage) ? "#ffffff" : "#52B788" }} />
                <ListItemText 
                  primary="Book Slot" 
                  primaryTypographyProps={{
                    fontWeight: (isBookingPage || isConfirmationPage) ? 700 : 600,
                    color: (isBookingPage || isConfirmationPage) ? "#ffffff" : "#52B788",
                  }}
                />
              </ListItemButton>
            </ListItem>
          </List>
        </Drawer>
      </AppBar>
    );
  }
);

ModernHeader.displayName = "ModernHeader";

export default ModernHeader;

